// Visual editor for label templates (Fase C do sprint impressão).
//
// Rota: /label-templates/editor/:id   (`:id = "new"` para criar)
//
// Layout em 3 colunas:
//   - Esquerda: paleta de campos + propriedades do elemento seleccionado
//   - Centro:   canvas Fabric.js com as dimensões do template em mm→px
//   - Direita:  propriedades globais (nome, dims, engine) + acções guardar
//
// Implementação é intencionalmente pragmática: suporta elementos
// `text`, `image` (company_logo), `barcode`. Não pretende replicar o
// motor de render do desktop — só produz um `layout_json` compatível.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as fabric from "fabric";

import Utils from "../../utils";
import {
  getPrintTemplate,
  createPrintTemplate,
  updatePrintTemplate,
  availableTemplateFields,
} from "../../services/LabelServices";
import "./editor.css";

// 1mm → n pixels for the canvas. A 4x multiplier gives good precision for
// typical label sizes (100×60mm → 400×240px canvas) while staying
// performant. Template dims are stored in mm; we only multiply in/out.
const MM_PX = 4;

const emptyLayout = () => ({
  canvas_mode: "fixed",
  elements: [],
});

const defaultTemplate = {
  name: "Novo template",
  description: "",
  entity_type: "part",
  width_mm: 100,
  height_mm: 60,
  dpi: 203,
  engine: "pdf",
  layout_json: emptyLayout(),
};

export default function LabelTemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";

  const canvasHostRef = useRef(null);
  const canvasRef = useRef(null); // fabric.Canvas instance
  const [template, setTemplate] = useState(defaultTemplate);
  const [fieldCatalog, setFieldCatalog] = useState({
    fields: [],
    image_sources: [],
    barcode_symbologies: [],
    categories: [],
  });
  const [selected, setSelected] = useState(null); // fabric object
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  // Draft strings para os inputs de dimensões. Permitem que o user
  // edite livremente (apague o campo, escreva "15" antes de "150" sem
  // ficar preso em "10") — o clamp só corre no blur. Sem isto, a
  // versão anterior chamava `Math.min(300, Math.max(10, ...))` a cada
  // keystroke e o input ficava praticamente inutilizável.
  const [dimDraft, setDimDraft] = useState({ w: "", h: "" });
  // Bumped on every selection change so the props panel re-renders even
  // when we mutate the same fabric object directly.
  const [, forceTick] = useState(0);
  const tick = () => forceTick((n) => n + 1);

  // Undo/Redo history — JSON snapshots of the canvas excluding grid lines.
  const historyRef = useRef({ stack: [], index: -1, suspend: false });
  const HISTORY_MAX = 50;
  const captureSnapshot = () => {
    const c = canvasRef.current;
    if (!c || historyRef.current.suspend) return;
    const json = c.toJSON(["data"]);
    const h = historyRef.current;
    // Truncate any forward history when a new edit lands.
    h.stack = h.stack.slice(0, h.index + 1);
    h.stack.push(JSON.stringify(json));
    if (h.stack.length > HISTORY_MAX) h.stack.shift();
    h.index = h.stack.length - 1;
  };
  const restoreSnapshot = (snapshot) => {
    const c = canvasRef.current;
    if (!c) return;
    historyRef.current.suspend = true;
    c.loadFromJSON(JSON.parse(snapshot)).then(() => {
      c.requestRenderAll();
      historyRef.current.suspend = false;
    });
  };
  const undo = () => {
    const h = historyRef.current;
    if (h.index <= 0) return;
    h.index -= 1;
    restoreSnapshot(h.stack[h.index]);
  };
  const redo = () => {
    const h = historyRef.current;
    if (h.index >= h.stack.length - 1) return;
    h.index += 1;
    restoreSnapshot(h.stack[h.index]);
  };

  // ── load template + field catalog ────────────────────────────────
  useEffect(() => {
    const loadFields = (entityType) =>
      availableTemplateFields({ entity_type: entityType })
        .then((r) => setFieldCatalog(r.data?.data || {}))
        .catch(() => {});

    if (isNew) {
      loadFields("part");
      setDimDraft({
        w: String(defaultTemplate.width_mm),
        h: String(defaultTemplate.height_mm),
      });
      return;
    }
    setLoading(true);
    getPrintTemplate({ id })
      .then((r) => {
        const t = r.data?.data;
        if (!t) throw new Error("Template não encontrado.");
        const layout =
          typeof t.layout_json === "string"
            ? JSON.parse(t.layout_json)
            : t.layout_json || emptyLayout();
        // Defensive clamp on load — if a legacy or hand-edited
        // template comes back with absurd dimensions (we've seen
        // 9999×9999 from a mistyped admin edit), the canvas render
        // below would lock the browser. The bounds match the input
        // fields: [10, 300] mm.
        const w = Math.min(300, Math.max(10, Number(t.width_mm) || 100));
        const h = Math.min(300, Math.max(10, Number(t.height_mm) || 60));
        setTemplate({
          ...t,
          width_mm: w,
          height_mm: h,
          dpi: Number(t.dpi) || 203,
          layout_json: layout,
        });
        setDimDraft({ w: String(w), h: String(h) });
        loadFields(t.entity_type || "part");
      })
      .catch((err) => toast.error(Utils.getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  // ── build fabric canvas ──────────────────────────────────────────
  useEffect(() => {
    if (!canvasHostRef.current || loading) return;
    const cw = template.width_mm * MM_PX;
    const ch = template.height_mm * MM_PX;
    const c = new fabric.Canvas(canvasHostRef.current, {
      width: cw,
      height: ch,
      backgroundColor: "#ffffff",
      preserveObjectStacking: true,
      selection: true,
    });
    canvasRef.current = c;

    // Draw a light grid as mm markers (every 10mm).
    const gridGroupKey = "__grid__";
    for (let x = 0; x <= template.width_mm; x += 10) {
      c.add(
        new fabric.Line([x * MM_PX, 0, x * MM_PX, ch], {
          stroke: "#e5e7eb",
          selectable: false,
          evented: false,
          data: { grid: true, key: gridGroupKey },
        }),
      );
    }
    for (let y = 0; y <= template.height_mm; y += 10) {
      c.add(
        new fabric.Line([0, y * MM_PX, cw, y * MM_PX], {
          stroke: "#e5e7eb",
          selectable: false,
          evented: false,
          data: { grid: true, key: gridGroupKey },
        }),
      );
    }

    // Hydrate canvas from the template layout.
    (template.layout_json?.elements || []).forEach((el) =>
      addFabricObjectForElement(c, el),
    );

    c.on("selection:created", (e) => setSelected(e.selected?.[0] || null));
    c.on("selection:updated", (e) => setSelected(e.selected?.[0] || null));
    c.on("selection:cleared", () => setSelected(null));
    // Snapshot capture on every meaningful change → drives undo/redo.
    c.on("object:added", (e) => {
      if (e.target?.data?.grid) return;
      captureSnapshot();
    });
    c.on("object:removed", (e) => {
      if (e.target?.data?.grid) return;
      captureSnapshot();
    });
    c.on("object:modified", () => captureSnapshot());

    // Initial snapshot so the first undo returns to the loaded state.
    captureSnapshot();

    return () => {
      c.dispose();
      canvasRef.current = null;
    };
    // Re-create when dims change so the canvas resizes without stale state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.width_mm, template.height_mm, loading]);

  // Keyboard shortcuts: Delete/Backspace removes selection,
  // Cmd/Ctrl+Z undo, Cmd/Ctrl+Shift+Z (or Cmd/Ctrl+Y) redo.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      // Don't hijack when typing in an input/textarea.
      if (["input", "textarea", "select"].includes(tag)) return;
      if (e.target?.isContentEditable) return;
      const c = canvasRef.current;
      if (!c) return;
      const mod = e.metaKey || e.ctrlKey;
      if ((e.key === "Delete" || e.key === "Backspace") && !mod) {
        const obj = c.getActiveObject();
        if (obj && !obj.data?.grid) {
          e.preventDefault();
          c.remove(obj);
          c.discardActiveObject();
          c.requestRenderAll();
        }
      } else if (mod && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (mod && (e.key.toLowerCase() === "y" ||
                         (e.key.toLowerCase() === "z" && e.shiftKey))) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── actions ──────────────────────────────────────────────────────
  const addText = (field) => {
    const c = canvasRef.current;
    if (!c) return;
    const obj = new fabric.Textbox(
      field?.example || "Texto",
      {
        left: 10 * MM_PX,
        top: 10 * MM_PX,
        width: 40 * MM_PX,
        fontSize: 12,
        fontFamily: "Helvetica",
        fill: "#111827",
        data: {
          tpType: "text",
          field: field?.key || null,
          prefix: "",
        },
      },
    );
    c.add(obj).setActiveObject(obj);
    c.requestRenderAll();
  };

  const addImage = (source) => {
    const c = canvasRef.current;
    if (!c) return;
    const w = 30 * MM_PX;
    const h = 15 * MM_PX;
    const obj = new fabric.Rect({
      left: 5 * MM_PX,
      top: 5 * MM_PX,
      width: w,
      height: h,
      fill: "rgba(139, 92, 246, 0.08)",
      stroke: "#8B5CF6",
      strokeDashArray: [4, 4],
      strokeWidth: 1,
      data: { tpType: "image", source },
    });
    c.add(obj).setActiveObject(obj);
    c.requestRenderAll();
  };

  const addBarcode = (symbology = "CODE128", field = "parts_internal_id") => {
    const c = canvasRef.current;
    if (!c) return;
    const obj = new fabric.Rect({
      left: 5 * MM_PX,
      top: 30 * MM_PX,
      width: 50 * MM_PX,
      height: 12 * MM_PX,
      fill: "rgba(129, 187, 66, 0.1)",
      stroke: "#81BB42",
      strokeDashArray: [4, 2],
      strokeWidth: 1,
      data: {
        tpType: "barcode",
        field,
        symbology,
      },
    });
    c.add(obj).setActiveObject(obj);
    c.requestRenderAll();
  };

  const removeSelected = () => {
    const c = canvasRef.current;
    if (!c || !selected) return;
    c.remove(selected);
    c.discardActiveObject();
    c.requestRenderAll();
  };

  const save = async () => {
    const c = canvasRef.current;
    if (!c) return;
    const elements = [];
    c.forEachObject((obj) => {
      if (obj.data?.grid) return;
      const d = obj.data || {};
      const base = {
        x: +(obj.left / MM_PX).toFixed(2),
        y: +(obj.top / MM_PX).toFixed(2),
        w: +((obj.width * (obj.scaleX || 1)) / MM_PX).toFixed(2),
        h: +((obj.height * (obj.scaleY || 1)) / MM_PX).toFixed(2),
      };
      if (d.tpType === "text") {
        elements.push({
          type: "text",
          field: d.field || undefined,
          computed: d.computed || undefined,
          prefix: d.prefix || undefined,
          suffix: d.suffix || undefined,
          align: d.align || undefined,
          font_size: obj.fontSize || 12,
          bold: obj.fontWeight === "bold" ? true : undefined,
          italic: obj.fontStyle === "italic" ? true : undefined,
          max_lines: d.max_lines || undefined,
          hide_if_empty: d.hide_if_empty || undefined,
          ...base,
        });
      } else if (d.tpType === "image") {
        elements.push({
          type: "image",
          source: d.source || "company_logo",
          fit: d.fit || "contain",
          ...base,
        });
      } else if (d.tpType === "barcode") {
        elements.push({
          type: "barcode",
          field: d.field || "parts_internal_id",
          symbology: d.symbology || "CODE128",
          ...base,
        });
      }
    });

    const layout = {
      ...(template.layout_json || {}),
      canvas_mode: template.layout_json?.canvas_mode || "fixed",
      elements,
    };

    setSaving(true);
    try {
      if (isNew) {
        const r = await createPrintTemplate({
          name: template.name,
          description: template.description,
          entity_type: template.entity_type,
          width_mm: template.width_mm,
          height_mm: template.height_mm,
          dpi: template.dpi,
          engine: template.engine,
          layout_json: layout,
        });
        toast.success("Template criado.");
        const newId = r.data?.data?.id;
        if (newId) navigate(`/label-templates/editor/${newId}`, { replace: true });
      } else {
        await updatePrintTemplate({
          id,
          name: template.name,
          description: template.description,
          width_mm: template.width_mm,
          height_mm: template.height_mm,
          dpi: template.dpi,
          engine: template.engine,
          layout_json: layout,
        });
        toast.success("Template actualizado.");
      }
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────
  const grouped = useMemo(() => {
    const byCat = {};
    (fieldCatalog.fields || []).forEach((f) => {
      (byCat[f.category] = byCat[f.category] || []).push(f);
    });
    return byCat;
  }, [fieldCatalog]);

  if (loading) {
    return <div className="tpl-editor-loading">A carregar template…</div>;
  }

  return (
    <div className="tpl-editor">
      {/* Left rail — palette + element props */}
      <aside className="tpl-editor-left">
        <h3>Paleta</h3>
        {(fieldCatalog.categories || []).map((cat) => {
          // Render the Imagens block right after "Empresa" so the logo +
          // company name are available together at the top of the palette.
          const fields = grouped[cat.key] || [];
          return (
            <React.Fragment key={cat.key}>
              {fields.length > 0 && (
                <div className="tpl-cat">
                  <div className="tpl-cat-label">{cat.label}</div>
                  {fields.map((f) => (
                    <button
                      type="button"
                      key={f.key}
                      className="tpl-field-btn"
                      onClick={() => addText(f)}
                      title={f.example}
                    >
                      + {f.label}
                    </button>
                  ))}
                </div>
              )}
              {cat.key === "company" && (
                <div className="tpl-cat">
                  <div className="tpl-cat-label">Imagens</div>
                  {(fieldCatalog.image_sources || []).map((s) => (
                    <button
                      type="button"
                      key={s.key}
                      className="tpl-field-btn"
                      onClick={() => addImage(s.key)}
                    >
                      + {s.label}
                    </button>
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        })}

        <div className="tpl-cat">
          <div className="tpl-cat-label">Códigos de barras</div>
          {(fieldCatalog.barcode_symbologies || []).map((s) => (
            <button
              type="button"
              key={s.key}
              className="tpl-field-btn"
              onClick={() => addBarcode(s.key)}
            >
              + {s.label}
            </button>
          ))}
        </div>

        {selected && (
          <div className="tpl-selected-box">
            <div className="tpl-selected-head">
              <span className="tpl-cat-label">Elemento</span>
              <button
                type="button"
                className="tpl-x-btn"
                title="Remover (tecla Delete)"
                onClick={removeSelected}
              >
                ×
              </button>
            </div>
            <div className="tpl-meta">
              <span>{labelForType(selected.data?.tpType)}</span>
              {selected.data?.field && (
                <code>{selected.data.field}</code>
              )}
            </div>

            {selected.data?.tpType === "text" && (
              <ElementPropsText
                obj={selected}
                onChange={(patch) => {
                  applyPatchToObject(selected, patch);
                  canvasRef.current?.requestRenderAll();
                  captureSnapshot();
                  tick();
                }}
              />
            )}
            {selected.data?.tpType === "barcode" && (
              <ElementPropsBarcode
                obj={selected}
                catalog={fieldCatalog}
                onChange={(patch) => {
                  applyPatchToObject(selected, patch);
                  canvasRef.current?.requestRenderAll();
                  captureSnapshot();
                  tick();
                }}
              />
            )}

            <div className="tpl-history-row">
              <button
                type="button"
                className="tpl-btn-ghost-sm"
                onClick={undo}
                title="Desfazer (Cmd/Ctrl+Z)"
              >
                ↶ Desfazer
              </button>
              <button
                type="button"
                className="tpl-btn-ghost-sm"
                onClick={redo}
                title="Refazer (Cmd/Ctrl+Shift+Z)"
              >
                ↷ Refazer
              </button>
            </div>
          </div>
        )}

        {!selected && (
          <div className="tpl-history-row tpl-history-floating">
            <button
              type="button"
              className="tpl-btn-ghost-sm"
              onClick={undo}
              title="Desfazer (Cmd/Ctrl+Z)"
            >
              ↶ Desfazer
            </button>
            <button
              type="button"
              className="tpl-btn-ghost-sm"
              onClick={redo}
              title="Refazer (Cmd/Ctrl+Shift+Z)"
            >
              ↷ Refazer
            </button>
          </div>
        )}
      </aside>

      {/* Canvas */}
      <main className="tpl-editor-main">
        <div className="tpl-canvas-wrap">
          <div
            className="tpl-canvas-ruler"
            style={{ width: template.width_mm * MM_PX }}
          >
            {template.width_mm}mm × {template.height_mm}mm
          </div>
          <canvas ref={canvasHostRef} />
        </div>
      </main>

      {/* Right rail — template props + save */}
      <aside className="tpl-editor-right">
        <h3>{isNew ? "Novo template" : "Editar template"}</h3>
        <label>Nome</label>
        <input
          type="text"
          value={template.name}
          onChange={(e) =>
            setTemplate((t) => ({ ...t, name: e.target.value }))
          }
        />
        <label>Tipo</label>
        <select
          value={template.entity_type}
          onChange={(e) =>
            setTemplate((t) => ({ ...t, entity_type: e.target.value }))
          }
          disabled={!isNew}
        >
          <option value="part">Peça</option>
          <option value="vehicle">Viatura</option>
        </select>
        <label>Largura (mm)</label>
        <input
          type="number"
          min={10}
          max={300}
          // Clamp acontece no blur, não no onChange. Sem isto, ao
          // digitar "150" o "1" era imediatamente forçado a 10 e o
          // user nunca conseguia chegar a três dígitos. Limites
          // apertados a 300mm (era 999) evitam ainda crash do canvas
          // Fabric — 300mm × MM_PX=4 dá um stage manuseável.
          value={dimDraft.w}
          onChange={(e) => setDimDraft((d) => ({ ...d, w: e.target.value }))}
          onBlur={() => {
            const n = Math.min(
              300,
              Math.max(10, Math.round(Number(dimDraft.w) || template.width_mm)),
            );
            setDimDraft((d) => ({ ...d, w: String(n) }));
            if (n !== template.width_mm) {
              setTemplate((t) => ({ ...t, width_mm: n }));
            }
          }}
        />
        <label>Altura (mm)</label>
        <input
          type="number"
          min={10}
          max={300}
          value={dimDraft.h}
          onChange={(e) => setDimDraft((d) => ({ ...d, h: e.target.value }))}
          onBlur={() => {
            const n = Math.min(
              300,
              Math.max(10, Math.round(Number(dimDraft.h) || template.height_mm)),
            );
            setDimDraft((d) => ({ ...d, h: String(n) }));
            if (n !== template.height_mm) {
              setTemplate((t) => ({ ...t, height_mm: n }));
            }
          }}
        />
        <label>Motor</label>
        <select
          value={template.engine}
          onChange={(e) =>
            setTemplate((t) => ({ ...t, engine: e.target.value }))
          }
        >
          <option value="pdf">PDF (universal)</option>
          <option value="zpl">ZPL (Zebra)</option>
        </select>

        <button
          type="button"
          className="tpl-btn-primary"
          onClick={save}
          disabled={saving}
        >
          {saving ? "A guardar…" : "Guardar"}
        </button>
        <button
          type="button"
          className="tpl-btn-ghost"
          onClick={() => navigate("/label-templates")}
        >
          Voltar à lista
        </button>
      </aside>
    </div>
  );
}

function labelForType(t) {
  switch (t) {
    case "text":
      return "Texto";
    case "image":
      return "Imagem";
    case "barcode":
      return "Código de barras";
    default:
      return t || "—";
  }
}

/// Apply a `{key: value}` patch to a fabric object covering both
/// fabric-native props and our `data.*` namespace transparently. Keeps
/// the prop panel concise on the call site.
function applyPatchToObject(obj, patch) {
  obj.data = obj.data || {};
  for (const [k, v] of Object.entries(patch)) {
    if (k === "fontSize") obj.set("fontSize", v);
    else if (k === "fontWeight") obj.set("fontWeight", v);
    else if (k === "fontStyle") obj.set("fontStyle", v);
    else if (k === "textAlign") obj.set("textAlign", v);
    else if (k === "splitByGrapheme") obj.set("splitByGrapheme", v);
    else obj.data[k] = v;
  }
  // Re-flow textboxes if word-wrap toggled.
  if ("wrap" in patch && obj.set) {
    obj.set("splitByGrapheme", false);
  }
  obj.setCoords?.();
}

function ElementPropsText({ obj, onChange }) {
  const d = obj.data || {};
  const wrap = d.wrap !== false; // default true
  return (
    <div className="tpl-props">
      <label>Alinhamento</label>
      <div className="tpl-align-row">
        {["left", "center", "right", "justify"].map((a) => (
          <button
            type="button"
            key={a}
            className={`tpl-align-btn${
              (obj.textAlign || "left") === a ? " is-active" : ""
            }`}
            onClick={() => onChange({ textAlign: a, align: a })}
            title={a}
          >
            {a === "left" && "⇤"}
            {a === "center" && "⇔"}
            {a === "right" && "⇥"}
            {a === "justify" && "⇆"}
          </button>
        ))}
      </div>

      <label>Tamanho</label>
      <input
        type="number"
        min={4}
        max={48}
        value={obj.fontSize || 10}
        onChange={(e) =>
          onChange({ fontSize: Number(e.target.value) || 10 })
        }
      />

      <label>Estilo</label>
      <div className="tpl-align-row">
        <button
          type="button"
          className={`tpl-align-btn${
            obj.fontWeight === "bold" ? " is-active" : ""
          }`}
          onClick={() =>
            onChange({
              fontWeight: obj.fontWeight === "bold" ? "normal" : "bold",
            })
          }
        >
          <b>B</b>
        </button>
        <button
          type="button"
          className={`tpl-align-btn${
            obj.fontStyle === "italic" ? " is-active" : ""
          }`}
          onClick={() =>
            onChange({
              fontStyle: obj.fontStyle === "italic" ? "normal" : "italic",
            })
          }
        >
          <i>I</i>
        </button>
      </div>

      <label>Comportamento de texto longo</label>
      <div className="tpl-toggle-row">
        <label className="tpl-toggle">
          <input
            type="radio"
            name={`wrap-${obj.id || obj.left}`}
            checked={wrap}
            onChange={() => onChange({ wrap: true, max_lines: undefined })}
          />
          Nova linha (wrap)
        </label>
        <label className="tpl-toggle">
          <input
            type="radio"
            name={`wrap-${obj.id || obj.left}`}
            checked={!wrap}
            onChange={() => onChange({ wrap: false, max_lines: 1 })}
          />
          Cortar (1 linha)
        </label>
      </div>

      <label>Prefixo</label>
      <input
        type="text"
        value={d.prefix || ""}
        onChange={(e) => onChange({ prefix: e.target.value })}
        placeholder="ex: 'Ref: '"
      />
      <label>Sufixo</label>
      <input
        type="text"
        value={d.suffix || ""}
        onChange={(e) => onChange({ suffix: e.target.value })}
        placeholder="ex: ' EUR'"
      />

      <label className="tpl-toggle">
        <input
          type="checkbox"
          checked={d.hide_if_empty === true}
          onChange={(e) => onChange({ hide_if_empty: e.target.checked })}
        />
        Ocultar se vazio
      </label>
    </div>
  );
}

function ElementPropsBarcode({ obj, catalog, onChange }) {
  const d = obj.data || {};
  const fields = (catalog?.fields || []).filter((f) =>
    ["main_reference", "parts_internal_id", "vehicle_internal_id"].includes(
      f.key,
    ),
  );
  return (
    <div className="tpl-props">
      <label>Símbologia</label>
      <select
        value={d.symbology || "CODE128"}
        onChange={(e) => onChange({ symbology: e.target.value })}
      >
        {(catalog?.barcode_symbologies || []).map((s) => (
          <option key={s.key} value={s.key}>
            {s.label}
          </option>
        ))}
      </select>
      <label>Campo a codificar</label>
      <select
        value={d.field || "parts_internal_id"}
        onChange={(e) => onChange({ field: e.target.value })}
      >
        {fields.map((f) => (
          <option key={f.key} value={f.key}>
            {f.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Turn a layout element definition into a Fabric object and add to
 * canvas. Non-destructive — only creates, caller decides when to add.
 */
function addFabricObjectForElement(c, el) {
  const left = (el.x ?? 0) * MM_PX;
  const top = (el.y ?? 0) * MM_PX;
  const width = (el.w ?? 20) * MM_PX;
  const height = (el.h ?? 6) * MM_PX;

  if (el.type === "text") {
    const obj = new fabric.Textbox(
      (el.prefix || "") + (el.field ? `{{${el.field}}}` : el.computed ? `{{${el.computed}}}` : ""),
      {
        left,
        top,
        width,
        fontSize: el.font_size || 10,
        fontFamily: "Helvetica",
        fontWeight: el.bold ? "bold" : "normal",
        fontStyle: el.italic ? "italic" : "normal",
        textAlign: el.align || "left",
        fill: "#111827",
        data: {
          tpType: "text",
          field: el.field,
          computed: el.computed,
          prefix: el.prefix,
          suffix: el.suffix,
          align: el.align,
          max_lines: el.max_lines,
          hide_if_empty: el.hide_if_empty,
        },
      },
    );
    c.add(obj);
  } else if (el.type === "image") {
    const obj = new fabric.Rect({
      left,
      top,
      width,
      height,
      fill: "rgba(139, 92, 246, 0.08)",
      stroke: "#8B5CF6",
      strokeDashArray: [4, 4],
      strokeWidth: 1,
      data: { tpType: "image", source: el.source, fit: el.fit },
    });
    c.add(obj);
  } else if (el.type === "barcode") {
    const obj = new fabric.Rect({
      left,
      top,
      width,
      height,
      fill: "rgba(129, 187, 66, 0.1)",
      stroke: "#81BB42",
      strokeDashArray: [4, 2],
      strokeWidth: 1,
      data: {
        tpType: "barcode",
        field: el.field,
        symbology: el.symbology,
      },
    });
    c.add(obj);
  }
}
